import React from 'react';
import { Card, CardContent, Avatar, Typography, Box, Button, Chip, IconButton } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GavelIcon from '@mui/icons-material/Gavel';
import { decrypt } from '../../../Security';
import { getRepresentativeImageUrl, handleImageError } from '../../../utils/imageUtils';

const VV = { primary: '#3F2B96', secondary: '#7F5AF0', accent: '#00C896', border: 'rgba(63,43,150,0.10)' };

const SingleRep = ({ rep, setRepId }) => {

  const handleEdit = () => {
    console.log('✏️ Edit button clicked for rep:', rep._id, rep.name);
    setRepId(rep._id);
  };

  const deleteRep = async () => {
    if (!window.confirm(`Delete representative "${rep.name}"?`)) return;
    try {
      const response = await fetch(process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/deleteRep/' + rep._id, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = decrypt(await response.json());
      if (data.msg === 'SUCCESS') alert('Representative deleted successfully!');
      else alert(JSON.stringify(data.error));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <Card sx={{
      borderRadius: '16px',
      border: `1.5px solid ${VV.border}`,
      boxShadow: '0 4px 20px rgba(63,43,150,0.08)',
      transition: 'all 0.25s',
      '&:hover': { boxShadow: '0 8px 32px rgba(63,43,150,0.15)', transform: 'translateY(-4px)' },
      overflow: 'hidden',
    }}>
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Avatar
            src={getRepresentativeImageUrl(rep.imagePath, 'large')}
            onError={(e) => handleImageError(e, 'large')}
            sx={{
              width: 72,
              height: 72,
              border: `3px solid ${VV.secondary}`,
              boxShadow: `0 0 0 4px ${VV.bg}`,
              fontSize: 28,
              fontWeight: 700,
              bgcolor: `${VV.primary}22`,
              color: VV.primary,
            }}
          >
            {!rep.imagePath && (rep.name ? rep.name[0].toUpperCase() : <PersonIcon sx={{ fontSize: 32 }} />)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: VV.primary, mb: 0.5, lineHeight: 1.3 }}>
              {rep.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <EmailIcon sx={{ fontSize: 14, color: '#888' }} />
              <Typography variant="caption" sx={{ color: '#666' }}>{rep.official_email_id || 'N/A'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {rep.party && (
                <Chip icon={<GavelIcon sx={{ fontSize: 12 }} />} label={rep.party} size="small"
                  sx={{ bgcolor: '#EDE7F6', color: VV.primary, fontWeight: 600, borderRadius: '6px', fontSize: '0.65rem', height: 22 }} />
              )}
              {rep.state && (
                <Chip icon={<LocationOnIcon sx={{ fontSize: 12 }} />} label={rep.state} size="small"
                  sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, borderRadius: '6px', fontSize: '0.65rem', height: 22 }} />
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ bgcolor: VV.bg, borderRadius: '10px', p: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <GavelIcon sx={{ fontSize: 16, color: VV.secondary }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: VV.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>Position</Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>{rep.current_elected_position || 'N/A'}</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <BusinessIcon sx={{ fontSize: 16, color: VV.accent }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: VV.accent, textTransform: 'uppercase', letterSpacing: 0.5 }}>Admin Area</Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>{rep.administrative_area || 'N/A'}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<EditIcon />}
            onClick={handleEdit}
            variant="contained"
            fullWidth
            sx={{
              borderRadius: '10px',
              background: `linear-gradient(135deg, ${VV.primary}, ${VV.secondary})`,
              fontWeight: 700,
              fontSize: '0.8rem',
              boxShadow: '0 4px 12px rgba(63,43,150,0.25)',
              '&:hover': { boxShadow: '0 6px 20px rgba(63,43,150,0.35)' },
            }}
          >
            Edit
          </Button>
          <IconButton
            onClick={deleteRep}
            sx={{
              bgcolor: '#FFEBEE',
              color: '#C62828',
              borderRadius: '10px',
              '&:hover': { bgcolor: '#FFCDD2', transform: 'scale(1.05)' },
              transition: 'all 0.2s',
            }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Card>
  );
};

export default SingleRep;
